import { Button, Modal } from 'react-bootstrap';
import CloseIcon from '@/assets/icon-close.svg?react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { TreeDropdown } from '../common/UI/TreeNode';
import {
  getFinanceAgentLocations,
  getFinanceAgentPermissions,
  getProfitCenters,
} from '@/services/aiApps';
import CheveronIcon from '@/assets/icon-cheveron.svg?react';
import { FINANCE_APP_PERMISSIONS } from '@/config/settings';
import { AuthContext } from '@/context/AuthContext';
import OverlayTooltip from '../common/UI/OverlayTooltip';
import FinanceAppIcon from '@/assets/icon-finance-app.svg?react';
import useElysiaStore from '@/app/elysia-store';

const ConfigureFinanceAppModal = ({ initiated, setInitiated }) => {
  const handleClose = () => {
    setShowModal(false);
  };
  const [profitCenterData, setProfitCenterData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [locationData, setLocationData] = useState([]);
  const [showPermissions, setShowPermissions] = useState(false);
  const [permissionData, setPermissionData] = useState({ text: '', link: '' });
  const [isWideScreen, setIsWideScreen] = useState(() => window.innerWidth >= 768);
  const [selectProfitCenter, setSelectedProfitCenter] = useState<any>({});
  const [selectedLocation, setSelectedLocation] = useState<any>({});
  const [isButtonSelected, setIsButtonSelected] = useState(false);
  const [restrictedTables, setRestrictedTables] = useState([]);
  const [tableFilters, setTableFilters] = useState<string[]>([]);
  const { profileInfo } = useContext(AuthContext);

  const profitCenter = useElysiaStore((state) => state.profitCenter);
  const financeAppLocation = useElysiaStore((state) => state.financeAppLocation);
  const setProfitCenter = useElysiaStore.getState().setProfitCenter;
  const setFinanceAppLocation = useElysiaStore.getState().setFinanceAppLocation;

  useEffect(() => {
    if (initiated) {
      setShowModal(true);
    }
  }, [initiated]);

  useEffect(() => {
    const handleResize = () => {
      setIsWideScreen(window.innerWidth >= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const initProfitCenters = async () => {
      setIsButtonSelected(false);
      try {
        const res = (await getProfitCenters(profileInfo?.userId || '')) ?? [];
        if (res?.data?.length === 0) {
          await getPermissions();
        }
        setProfitCenterData(res?.data);
      } catch (err) {
        console.error(err);
        setProfitCenterData([]);
      }
      getLocations();
    };

    initProfitCenters();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (showModal) {
      setSelectedProfitCenter(profitCenter);
      setSelectedLocation(financeAppLocation);
    }
    if (profitCenter?.value?.length || financeAppLocation?.value?.length) {
      setIsButtonSelected(true);
    } else if (!profitCenter?.value?.length || !financeAppLocation?.value?.length) {
      setIsButtonSelected(false);
    }
  }, [showModal, profitCenter, financeAppLocation]);

  const getLocations = useCallback(async () => {
    if (!locationData?.length) {
      try {
        const locationRes = await getFinanceAgentLocations(profileInfo?.userId || '');
        setLocationData(locationRes?.data ?? []);
      } catch {
        setLocationData([]);
      }
    }
    // eslint-disable-next-line
  }, [locationData]);

  const handleProfitSelect = (e) => {
    setSelectedProfitCenter(e);
  };

  const handleLocationSelect = (e) => {
    setSelectedLocation(e);
  };

  const extractTableFilters = (rawFilters: unknown): string[] => {
    if (!Array.isArray(rawFilters) || rawFilters.length === 0) return [];

    const allFiltersSet = new Set<string>();

    for (const entry of rawFilters) {
      if (!entry) continue;

      for (const value of Object.values(entry)) {
        if (!Array.isArray(value)) continue;

        for (const f of value) {
          if (typeof f !== 'string') continue;

          const trimmed = f.trim();

          if (!trimmed || trimmed === '1=1') continue;

          allFiltersSet.add(trimmed);
        }
      }
    }

    return Array.from(allFiltersSet);
  };

  const getPermissions = async () => {
    if (!showPermissions) {
      setShowPermissions(true);
      try {
        const response = await getFinanceAgentPermissions(profileInfo?.userId || '');

        const denied = response?.denied_tables || [];
        const nonWildcardList = denied.filter((t) => t !== '*');
        const rawFilters = response?.table_filters || [];
        const allFilters = extractTableFilters(rawFilters);
        setTableFilters(allFilters);

        setRestrictedTables(nonWildcardList);
        if (denied.length === 1 && denied[0] === '*') {
          setPermissionData(FINANCE_APP_PERMISSIONS.noAccess);
        } else if (denied.length === 0) {
          setPermissionData(FINANCE_APP_PERMISSIONS.completeAccess);
        } else if (nonWildcardList?.length > 0) {
          setPermissionData(FINANCE_APP_PERMISSIONS.partialAccess);
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      setShowPermissions(false);
    }
  };

  const handleModalClick = () => {
    setInitiated(true);
    setShowModal(true);
  };

  const handleProced = () => {
    setFinanceAppLocation(selectedLocation);
    setProfitCenter(selectProfitCenter);
    handleClose();
  };

  const handleClearProfitCenter = () => {
    setSelectedProfitCenter({});
  };

  const handleClearLocation = () => {
    setSelectedLocation({});
  };
  return (
    <>
      <OverlayTooltip
        tooltipText="Selections and Permissions"
        buttonContent={<FinanceAppIcon />}
        ariaLabel="finance app"
        testId="finance-app"
        tooltipClassName="tooltip-sidebar"
        buttonClassName={`finance-app ${isButtonSelected ? 'selected' : ''} `}
        placement="top-start"
        onClick={handleModalClick}
      />
      {showModal && (
        <Modal
          show={showModal}
          onHide={handleClose}
          className="image-generator-modal fin-app-modal"
          dialogClassName="custom-modal"
          centered={isWideScreen}
          backdrop="static"
        >
          <Modal.Header>
            <div className="header-content">
              <div>
                <Modal.Title>Finance Agent</Modal.Title>
              </div>
              <Button type="button" className="btn-close" onClick={handleClose} aria-label="Close">
                <CloseIcon />
              </Button>
            </div>
            <div className="instruction-row">
              <span>Select the options below for more precise answers instantly.</span>
              <span>If not needed, leave unselected.</span>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="mt-4">
              <p className="select-label">Profit Centre</p>
              <TreeDropdown
                data={profitCenterData}
                selectedId={selectProfitCenter?.value}
                onSelect={handleProfitSelect}
                placeholder="Select profit centre"
                onClear={handleClearProfitCenter}
                disabled={profitCenterData?.length === 0}
              />
            </div>
            <div className="mt-4">
              <p className="select-label">Location</p>
              <TreeDropdown
                data={locationData}
                selectedId={selectedLocation?.value}
                onSelect={handleLocationSelect}
                placeholder="Select Locations"
                onDropdownOpen={getLocations}
                onClear={handleClearLocation}
                disabled={profitCenterData?.length === 0}
              />
            </div>
            <div className="mt-4">
              <div
                className="d-flex justify-center align-items-center data-permissions"
                onClick={() => getPermissions()}
              >
                <p className="select-label">Data permissions</p>
                <button className="bg-transparent border-0 shadow-none p-0 pb-1 ps-1">
                  <CheveronIcon className={`${showPermissions ? '' : 'accordian-cheveron'}`} />
                </button>
              </div>

              {showPermissions && (
                <>
                  {permissionData?.text?.length > 0 && (
                    <p className="permission-msg">
                      {permissionData?.text}
                      <a href={permissionData?.link} target="_blank" rel="noopener noreferrer">
                        {permissionData?.link}
                      </a>
                    </p>
                  )}

                  {restrictedTables?.length > 0 && (
                    <div>
                      <p className="mb-2">Denied tables</p>
                      <ul>
                        {restrictedTables.map((t) => (
                          <li key={t}>{t}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {tableFilters?.length > 0 && (
                    <div className="mt-2">
                      <p className="mb-2">Row level filter</p>
                      <ul>
                        {tableFilters.map((t, idx) => (
                          <li key={`filter-${idx}`}>{t}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="w-100 d-flex justify-content-between">
              <Button className="update-btn" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                className="update-btn proceed-btn"
                onClick={handleProced}
                disabled={
                  !(
                    selectedLocation.value !== financeAppLocation.value ||
                    selectProfitCenter.value !== profitCenter.value
                  )
                }
              >
                Proceed
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default ConfigureFinanceAppModal;
